import { Button } from '@stereograph/teia-system-design/components/Button';
import { Stack } from '@stereograph/teia-system-design/components/Stack';
import { Text } from '@stereograph/teia-system-design/components/Typography';
import { usePluginTranslation } from '@translation';
import { ChangeEventHandler, DragEvent, useRef } from 'react';
import { ANOMALY_FILE_SIZE_LIMIT } from '../document/AnomalyFileSizeStatus';
import { formatBytes } from '../../utils/formatBytes';

const ACCEPTED_FILES = {
  pdf: 'application/pdf',
  jpg: 'image/jpg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  mp4: 'video/mp4',
  json: 'application/json',
};
const ACCEPTED_FILES_TYPE = Object.values(ACCEPTED_FILES).join(',');
const ACCEPTED_FILES_EXTENSION = Object.keys(ACCEPTED_FILES);

interface DropzoneProps {
  onFileChange: (files: Array<File>) => void;
}

export const Dropzone = (props: DropzoneProps) => {
  const { onFileChange } = props;
  const { t } = usePluginTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDrop = (e: DragEvent) => {
    e.preventDefault();
    const files = e.dataTransfer.files;

    if (!files) {
      return;
    }

    onFileChange(Array.from(files));
  };

  const handleFileChange: ChangeEventHandler<HTMLInputElement> = (event) => {
    if (!event.target.files) {
      return;
    }

    onFileChange(Array.from(event.target.files));
  };

  return (
    <Stack
      component="label"
      alignItems="center"
      justifyContent="center"
      spacing={1}
      sx={{
        border: (theme) => `2px dashed ${theme.palette.divider}`,
        borderRadius: '4px',
        width: '100%',
        minHeight: '150px',
        cursor: 'pointer',
      }}
      onDragOver={(e) => e.preventDefault()}
      onDrop={handleDrop}
    >
      <Button
        component="span"
        data-dd-action-name="tvp.anomaly.document.upload"
      >
        {t('anomaly_plugin.document.upload.cta_label')}
      </Button>
      <Text component="span" variant="body2">
        {t('anomaly_plugin.document.upload.dragndrop_text')}
      </Text>
      <Text
        component="span"
        variant="body2"
        color="text.secondary"
        sx={{
          maxWidth: '60ch',
          textAlign: 'center',
          whiteSpace: 'break-spaces',
          mt: 3,
        }}
      >
        {t('anomaly_plugin.document.upload.instructions_text', {
          extensions: ACCEPTED_FILES_EXTENSION,
          size: formatBytes(ANOMALY_FILE_SIZE_LIMIT),
        })}
      </Text>
      <input
        type="file"
        style={{
          display: 'none',
        }}
        ref={fileInputRef}
        onChange={handleFileChange}
        multiple
        accept={ACCEPTED_FILES_TYPE}
      />
    </Stack>
  );
};
